import {
    AnyObject,
    FilterObject,
    FilterOperator,
    RangeValue,
} from '@common/types/queryState';
import { isEmpty } from '@common/utils/typeGuards';
import { BooleanPreset } from '../types';
import { FACET_MISSING_KEY } from '../constants';

export const isMissingId = (id: string) => id === FACET_MISSING_KEY;

export const getNormalizedFilterId = (
    filter: FilterObject,
    normalizer: (v: string) => string,
): string => {
    return filter.operator === FACET_MISSING_KEY
        ? FACET_MISSING_KEY
        : normalizer(filter.value);
};

export const normalizeBooleanPreset = (
    preset: string | BooleanPreset,
): BooleanPreset => {
    if (typeof preset === 'string') return { value: preset, operator: 'exact' };
    return preset;
};

// --- COMPARISON PREDICATES ---

export const doesFiltersMatch = (
    filterA: FilterObject,
    filterB: FilterObject,
): boolean =>
    filterA.value === filterB.value && filterA.operator === filterB.operator;

export const isFilterActive = (
    activeFilters: FilterObject[] | undefined,
    targetFilter: FilterObject,
): boolean => {
    if (!activeFilters || activeFilters.length === 0) return false;
    return activeFilters.some((item) => doesFiltersMatch(item, targetFilter));
};

export const doesRangesMatch = (
    rangeA: RangeValue,
    rangeB: RangeValue,
): boolean =>
    rangeA.min === rangeB.min &&
    rangeA.max === rangeB.max &&
    rangeA.operator === rangeB.operator;

export const isRangeFilterActive = (
    activeRange: RangeValue | undefined,
    targetRange: RangeValue,
): boolean => {
    if (!activeRange) return false;
    return doesRangesMatch(activeRange, targetRange);
};

export const isRangeDefined = (range: RangeValue | undefined): boolean =>
    range?.max !== undefined || range?.min !== undefined;

export const isCoveredBy = (
    value: string,
    pattern: string,
    operator: FilterOperator,
): boolean => {
    switch (operator) {
        case 'contains':
            return value.includes(pattern);
        case 'startsWith':
            return value.startsWith(pattern);
        case 'endsWith':
            return value.endsWith(pattern);
        default:
            return value === pattern;
    }
};

// --- Handle state ---

export const resolveToggledFilters = (
    currentItems: FilterObject[] = [],
    target: FilterObject | FilterObject[],
): FilterObject[] => {
    const targets = Array.isArray(target) ? target : [target];

    return targets.reduce<FilterObject[]>((acc, currentTarget) => {
        const { value, operator = 'exact' } = currentTarget;
        const isActive = isFilterActive(acc, currentTarget);

        // Toggle off the selected values
        if (isActive) {
            return acc.filter((item) => !doesFiltersMatch(item, currentTarget));
        }

        // When selecting 'missing', clear everything else for this field
        if (operator === 'missing') {
            return [currentTarget];
        }

        // Logic for adding a filter while cleaning up broad/specific conflicts
        const filteredAcc = acc.filter((item) => {
            const itemOp = item.operator || 'exact';

            // When selecting a normal value, remove any 'missing' filter
            if (itemOp === 'missing') return false;

            // When selecting a specific value, remove any broad pattern that covers it
            if (
                operator === 'exact' &&
                itemOp !== 'exact' &&
                isCoveredBy(value, item.value, itemOp)
            ) {
                return false;
            }

            // When selecting a broad patern, remove any specific value that it covers
            if (
                operator !== 'exact' &&
                itemOp === 'exact' &&
                isCoveredBy(item.value, value, operator)
            ) {
                return false;
            }

            return true;
        });

        return [...filteredAcc, currentTarget];
    }, currentItems);
};

export const resolveRangeFilter = (
    currentRange: RangeValue | undefined,
    target: RangeValue,
): RangeValue => {
    const operator = target.operator;

    // Check if we are toggling the 'missing' state
    if (operator === 'missing') {
        const isAlreadyMissing = currentRange?.operator === 'missing';
        return isAlreadyMissing ? {} : { operator: 'missing' };
    }

    const alreadyExists = isRangeFilterActive(currentRange, target);
    const isDefined = isRangeDefined(target);

    // Toggle off if it already exists
    return !alreadyExists && isDefined ? target : {};
};

export const updateOrDelete = <T extends AnyObject, K extends keyof T>(
    prev: T,
    key: K,
    value: T[keyof T] | undefined | null,
): T => {
    const next = { ...prev };
    if (isEmpty(value)) delete next[key];
    else next[key] = value as T[K];
    return next;
};
